import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import localUtil from '../../lib/util';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

@observer
class LotTestingList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            searchQuery: '',
            sortField: null,
            sortDirection: null,
        };
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = async () => {
        const { lotTestingStore } = this.props.stores;
        await lotTestingStore.load({});
    }

    handleSearch = (e) => {
        this.setState({ searchQuery: e.target.value });
    }

    handleSort = (field) => {
        const { sortField, sortDirection } = this.state;
        if (sortField === field) {
            if (sortDirection === 'asc') {
                this.setState({ sortDirection: 'desc' });
            } else {
                this.setState({ sortField: null, sortDirection: null });
            }
        } else {
            this.setState({ sortField: field, sortDirection: 'asc' });
        }
    }

    getSortValue = (lot, field) => {
        const firstTest = (lot.weaponTests || [])[0];
        if (field === 'rating') {
            return lot.rating || null;
        }
        if (field === 'speed') {
            if (!firstTest || !firstTest.speed) return null;
            return firstTest.speed;
        }
        if (field === 'es') {
            if (!firstTest || !firstTest.speedMin || !firstTest.speedMax) return null;
            return firstTest.speedMax - firstTest.speedMin;
        }
        if (field === 'sd') {
            if (!firstTest || !firstTest.speedStdDev) return null;
            return firstTest.speedStdDev;
        }
        return null;
    }

    sortList = (list) => {
        const { sortField, sortDirection } = this.state;
        if (!sortField || !sortDirection) return list;

        return [...list].sort((a, b) => {
            const valA = this.getSortValue(a, sortField);
            const valB = this.getSortValue(b, sortField);

            // Nulls always sort last
            if (valA === null && valB === null) return 0;
            if (valA === null) return 1;
            if (valB === null) return -1;

            const diff = valA - valB;
            return sortDirection === 'asc' ? diff : -diff;
        });
    }

    viewLotTesting = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('tr').dataset;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;

        appState.openDrawer('viewLotTesting', {
            height: drawerHeightMediumLarge,
            id: parseInt(id, 10),
            callback: () => {
                const { lotTestingStore } = this.props.stores;
                lotTestingStore.load({});
            },
        }, 1);
    }

    editLotTesting = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('tr').dataset;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;

        appState.openDrawer('editLotTesting', {
            height: drawerHeightMediumLarge,
            id: parseInt(id, 10),
            callback: () => {
                const { lotTestingStore } = this.props.stores;
                lotTestingStore.load({});
            },
        }, 1);
    }

    deleteLotTesting = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const dialogAction = beginDialogAction(this, 'deleteLotTesting');
        if (!dialogAction) return false;
        try {


            const { id } = e.target.closest('button').dataset;

            if (((await appConfirm('Are you sure you want to delete this LOT testing entry?')) && dialogAction.isCurrent())) {
                const { lotTestingStore } = this.props.stores;
                await lotTestingStore.delete(parseInt(id, 10));
                await lotTestingStore.load({});
            }
        } finally {
            dialogAction.finish();
        }
    }

    renderSortIcon = (field) => {
        const { sortField, sortDirection } = this.state;
        if (sortField !== field) return null;
        return (
            <i class={`fa-solid fa-caret-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`} />
        );
    }

    render() {
        const { searchQuery, sortField } = this.state;
        const { lotTestingStore, userStore } = this.props.stores;
        const { lotTestings = [], total = 0 } = lotTestingStore;
        const { user = {}, isAdmin } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        // Filter by search query
        let filteredList = lotTestings;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filteredList = lotTestings.filter(lot =>
                (lot.lotNumber && lot.lotNumber.toLowerCase().includes(query)) ||
                (lot.brand && lot.brand.toLowerCase().includes(query)) ||
                (lot.caliber && lot.caliber.toLowerCase().includes(query)) ||
                (lot.bulletBrand && lot.bulletBrand.toLowerCase().includes(query)) ||
                (lot.bulletType && lot.bulletType.toLowerCase().includes(query))
            );
        }

        // Apply sorting
        filteredList = this.sortList(filteredList);

        return (
            <div class='lot-testing-list'>
                {/* Search input */}
                <div class='mb-3'>
                    <div class='input-group'>
                        <span class='input-group-text'>
                            <i class='fa-solid fa-search' />
                        </span>
                        <input
                            type='text'
                            class='form-control'
                            placeholder='Search LOT number, brand, caliber...'
                            value={searchQuery}
                            onInput={this.handleSearch}
                        />
                    </div>
                </div>

                {/* Results count */}
                <div class='mb-2 text-muted small'>
                    <Text id='lottesting.showing'>Showing</Text> {filteredList.length} <Text id='lottesting.of'>of</Text> {total} <Text id='lottesting.entries'>entries</Text>
                </div>

                {/* List */}
                {filteredList.length === 0 ? (
                    <div class='text-center py-5'>
                        <i class='fa-solid fa-flask fa-3x mb-3 text-muted' />
                        <p class='text-muted'>
                            <Text id='lottesting.no-entries'>No LOT testing entries found.</Text>
                        </p>
                    </div>
                ) : (
                    <div class='table-responsive'>
                        <table class={`table table-hover ${darkmode ? 'table-dark' : ''}`}>
                            <thead>
                                <tr>
                                    <th><Text id='lottesting.ammunition'>Ammunition</Text></th>
                                    <th
                                        class={sortField === 'speed' ? 'text-primary' : ''}
                                        style='cursor: pointer;'
                                        onClick={() => this.handleSort('speed')}
                                    >
                                        Speed{this.renderSortIcon('speed')}
                                    </th>
                                    <th
                                        class={`text-center ${sortField === 'es' ? 'text-primary' : ''}`}
                                        style='cursor: pointer;'
                                        onClick={() => this.handleSort('es')}
                                    >
                                        ES{this.renderSortIcon('es')}
                                    </th>
                                    <th
                                        class={`text-center ${sortField === 'sd' ? 'text-primary' : ''}`}
                                        style='cursor: pointer;'
                                        onClick={() => this.handleSort('sd')}
                                    >
                                        SD{this.renderSortIcon('sd')}
                                    </th>
                                    <th
                                        class={`text-center ${sortField === 'rating' ? 'text-primary' : ''}`}
                                        style='cursor: pointer;'
                                        onClick={() => this.handleSort('rating')}
                                    >
                                        <Text id='lottesting.rating'>Rating</Text>{this.renderSortIcon('rating')}
                                    </th>
                                    <th class='text-center'><Text id='lottesting.tests'>Tests</Text></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map(lot => {
                                    const canEdit = isAdmin || lot.user === user.id;
                                    const weaponTestsCount = (lot.weaponTests || []).length;

                                    return (
                                        <tr
                                            key={lot.id}
                                            data-id={lot.id}
                                            onClick={this.viewLotTesting}
                                            style='cursor: pointer;'
                                        >
                                            <td>
                                                <div class='d-flex flex-column'>
                                                    <span class='fw-bold'>
                                                        {lot.brand || '-'}
                                                        {lot.isPublic && (
                                                            <i class='fa-solid fa-globe text-success ms-2' title='Public' style='font-size: 0.75rem;' />
                                                        )}
                                                    </span>
                                                    <span class='text-muted small'>
                                                        {lot.caliber}
                                                        {lot.lotNumber && <span class='ms-2 opacity-75'>LOT: {lot.lotNumber}</span>}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const firstTest = (lot.weaponTests || [])[0];
                                                    if (!firstTest || !firstTest.speed) return <span class='text-muted'>-</span>;
                                                    return (
                                                        <span class='badge bg-primary'>
                                                            {firstTest.speed} {firstTest.speedUnit || 'fps'}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td class='text-center align-middle'>
                                                {(() => {
                                                    const firstTest = (lot.weaponTests || [])[0];
                                                    if (!firstTest || !firstTest.speedMin || !firstTest.speedMax) return <span class='text-muted'>-</span>;
                                                    const es = firstTest.speedMax - firstTest.speedMin;
                                                    return (
                                                        <span class='badge bg-warning text-dark'>
                                                            {es}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td class='text-center align-middle'>
                                                {(() => {
                                                    const firstTest = (lot.weaponTests || [])[0];
                                                    if (!firstTest || !firstTest.speedStdDev) return <span class='text-muted'>-</span>;
                                                    return (
                                                        <span class='badge bg-info text-dark'>
                                                            {firstTest.speedStdDev}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td class='text-center align-middle'>
                                                {lot.rating ? (
                                                    <span class='text-warning' style='white-space: nowrap;'>
                                                        {[1, 2, 3, 4, 5].map(star => (
                                                            <i
                                                                key={star}
                                                                class={`fa-${star <= lot.rating ? 'solid' : 'regular'} fa-star`}
                                                                style='font-size: 0.75rem;'
                                                            />
                                                        ))}
                                                    </span>
                                                ) : (
                                                    <span class='text-muted'>-</span>
                                                )}
                                            </td>
                                            <td class='text-center align-middle'>
                                                <span class={`badge ${weaponTestsCount > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                                    {weaponTestsCount}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }
}

export default LotTestingList;
